import request from '../request';
/**
 * @typedef {Object} Fee
 * @property {number} feeid - The unique identifier for the fee
 * @property {string} description - The description of the fee (e.g., 'Card', 'ACH', 'Application Fee')
 * @property {string} processorname - The processor name (e.g., 'Stripe', 'Softrip')
 * @property {string|null} paymentref - Payment reference (e.g., 'card', 'us_bank_account')
 * @property {string} feetype - Type of fee ('fixed' or 'percentage')
 * @property {number} feevalue - The fee value (percentage or fixed amount)
 * @property {number|null} cap - Maximum fee amount (null if no cap)
 * @property {boolean} isvisible - Whether the fee is visible
 * @property {boolean} isenabled - Whether the fee is enabled
 * @property {boolean} isincluded - Whether the fee is included in the price
 * @property {string} [name] - Fee name (for backward compatibility)
 * @property {number} [id] - Alternative ID field (for backward compatibility)
 */

/**
 * @typedef {Object} FeeUpdate
 * @property {number} feeid - The unique identifier for the fee
 * @property {boolean} isincluded - Whether the fee is included in the price
 * @property {string} [description] - The description of the fee
 */

/**
 * @typedef {Object} ApiResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Array<Fee>|null} data - Response data
 * @property {string|null} [error] - Error message if request failed
 */

/**
 * @typedef {Object} FeeAllocation
 * @property {string} creditCardFeeAllocation - 'organizer' or 'passenger'
 * @property {string} achFeeAllocation - 'organizer' or 'passenger'
 * @property {string} platformFeeAllocation - 'organizer' or 'passenger'
 */

/**
 * Get trip fees for a specific trip
 * @param {number} tripId - The trip ID
 * @returns {Promise<ApiResponse>} API response with trip fees
 */
export const getTripFees = async (tripId) => {
  try {
    const response = await request.get(`trips/${tripId}`);
    // Extract fees from trip data - fees are included in the trip response
    return {
      success: true,
      data: response.data?.fees || [],
      error: null
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch trip fees';
    console.error('Error fetching trip fees:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Update trip fees inclusion settings
 * @param {number} tripId - The trip ID
 * @param {Array<{feeid: number, isincluded: boolean}>} fees - Array of fee objects with feeid and isincluded properties
 * @returns {Promise<{success: boolean, data: Array<Fee>|null, error: string|null}>} API response
 */
export const updateTripFees = async (tripId, fees) => {
  try {
    const response = await request.put(`trips/${tripId}/fee`, { fees });
    return {
      success: true,
      data: response.data?.data?.fee || [],
      error: null
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to update trip fees';
    console.error('Error updating trip fees:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * @typedef {Object.<string, Fee[]>} FeeGroups - Object mapping payment references to arrays of fees
 */

/**
 * Groups fees by their payment reference
 * @param {Array<Fee>} fees - Array of fee objects
 * @returns {FeeGroups} Object with paymentref as keys and arrays of fees as values
 */
const groupFeesByPaymentRef = (fees) => {
  /** @type {FeeGroups} */
  const initialValue = {};

  return fees.reduce((groups, fee) => {
    const key = fee.paymentref || 'platform'; // Use 'platform' for null paymentref
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(fee);
    return groups;
  }, initialValue);
};

/**
 * Get global fees (lookup.fee table) grouped by payment reference
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     allFees: Array<Fee>,
 *     feeGroups: { [key: string]: Array<Fee> },
 *     feeOptions: Array<{ value: string, labelKey: string, feeIds: string[] }>
 *   }|null,
 *   error: string|null
 * }>} API response with global fees and grouped data
 */
export const getGlobalFees = async () => {
  try {
    const response = await request.get('fees');
    const allFees = response.data?.fees || [];
    const feeGroups = groupFeesByPaymentRef(allFees);

    // Create fee options for the UI
    const feeOptions = Object.entries(feeGroups).map(([paymentRef, fees]) => {
      const feeIds = fees.map((fee) => fee.feeid.toString());
      return {
        value: paymentRef,
        labelKey: paymentRef ? `FeeLabel.${paymentRef}` : 'FeeLabel.platform',
        feeIds
      };
    });

    return {
      success: true,
      data: {
        allFees,
        feeGroups,
        feeOptions
      },
      error: null
    };
  } catch (/** @type {any} */ error) {
    console.error('Error fetching global fees:', error);
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to fetch global fees';
    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }
};

/**
 * Update global fees inclusion settings
 * @param {Array<{feeid: number, isincluded: boolean}>} fees - Array of fee objects with feeid and isincluded properties
 * @returns {Promise<{success: boolean, data: Array<Fee>|null, error: string|null}>} API response
 */
/**
 * @typedef {Object} UpdateFeeRequest
 * @property {number} feeid - The fee ID to update
 * @property {boolean} isincluded - Whether the fee is included in the price
 * @property {string} [description] - Fee description
 */

/**
 * Update global fees in the system
 * @param {Array<UpdateFeeRequest>} fees - Array of fee updates to apply
 * @returns {Promise<{success: boolean, data: Array<Fee>|null, error: string|null}>} Result of the operation
 */
export const updateGlobalFees = async (fees) => {
  try {
    // Ensure we have an array of fees to update
    if (!Array.isArray(fees) || fees.length === 0) {
      console.warn('No valid fees provided for update');
      return {
        success: true,
        data: [],
        error: null
      };
    }

    // Prepare the request payload
    const payload = {
      fees: fees.map((fee) => ({
        feeid: fee.feeid,
        isincluded: fee.isincluded,
        description: fee.description
      }))
    };

    const response = await request.put('fee/assign', payload);

    // Handle different response structures
    const updatedFees =
      response.data?.data?.fees || response.data?.data?.fee || [];

    return {
      success: true,
      data: Array.isArray(updatedFees) ? updatedFees : [updatedFees],
      error: null
    };
  } catch (/** @type {any} */ error) {
    console.error('Error updating global fees:', error);
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update global fees';
    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }
};

/**
 * Create a new global fee
 * @param {Object} feeData - Fee data object
 * @returns {Promise<ApiResponse>} API response
 */
export const createGlobalFee = async (feeData) => {
  try {
    const response = await request.post('fees', feeData);
    return {
      success: true,
      data: response.data?.data || null,
      error: null
    };
  } catch (/** @type {any} */ error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to create global fee';
    console.error('Error creating global fee:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Delete a global fee
 * @param {number} feeId - The fee ID to delete
 * @returns {Promise<{success: boolean, data: {id: number}|null, error: string|null}>} API response
 */
export const deleteGlobalFee = async (feeId) => {
  try {
    await request.delete(`fees/${feeId}`);
    return {
      success: true,
      data: { id: feeId },
      error: null
    };
  } catch (/** @type {any} */ error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete global fee';
    console.error('Error deleting global fee:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * @typedef {Object} FeeGroupUpdate
 * @property {string} paymentRef - The payment reference (e.g., 'card', 'us_bank_account', 'platform' for null)
 * @property {string[]} feeIds - Array of fee IDs in this group
 * @property {boolean} isIncluded - Whether the fee group is included in the price
 */

/**
 * Update fee allocation settings for tenant-level (global) fees using the new grouped approach
 * @param {string} paymentRef - The payment reference (e.g., 'card', 'us_bank_account', 'platform' for null)
 * @param {boolean} isIncluded - Whether the fee group should be included in the price
 * @returns {Promise<{success: boolean, data: Array<Fee>|null, error: string|null}>} API response
 */
export const updateGlobalFeeAllocations = async (paymentRef, isIncluded) => {
  try {
    // Fetch all global fees with grouping
    const { data, error: fetchError } = await getGlobalFees();
    if (fetchError || !data) {
      throw new Error(fetchError || 'Failed to fetch global fees');
    }

    const { feeGroups } = data;

    // Get the fee group for this payment reference
    const feeGroupKey = paymentRef ?? 'platform';
    const feeGroup = feeGroups[feeGroupKey];

    if (!feeGroup || feeGroup.length === 0) {
      throw new Error(`No fees found for payment reference: ${paymentRef}`);
    }

    // Create fee updates for all fees in this group
    const feesToUpdate = feeGroup.map((/** @type {Fee} */ fee) => ({
      feeid: fee.feeid,
      isincluded: isIncluded,
      description: fee.description
    }));

    // Update the fees
    const { data: updatedFees, error: updateError } =
      await updateGlobalFees(feesToUpdate);

    if (updateError) {
      throw new Error(updateError);
    }

    return {
      success: true,
      data: updatedFees || [],
      error: null
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to update global fee allocations';
    console.error('Error updating global fee allocations:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Get the current fee allocation setting for a payment reference
 * @param {string} paymentRef - The payment reference (e.g., 'card', 'us_bank_account', 'platform' for null)
 * @returns {Promise<{success: boolean, isIncluded: boolean|null, error: string|null}>}
 */
export const getFeeAllocationForPaymentRef = async (paymentRef) => {
  try {
    const { data, error: fetchError } = await getGlobalFees();
    if (fetchError || !data) {
      const errorMsg = fetchError || 'Failed to fetch global fees';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    const { feeGroups } = data;
    const feeGroupKey =
      paymentRef === null || paymentRef === 'platform'
        ? 'platform'
        : paymentRef;
    const feeGroup = feeGroups[feeGroupKey];

    if (!feeGroup || feeGroup.length === 0) {
      return {
        success: true,
        isIncluded: false, // Default to false if no fees found
        error: null
      };
    }

    // If any fee in the group is included, consider the group as included
    const isIncluded = feeGroup.some((/** @type {Fee} */ fee) => {
      return fee.isincluded === true;
    });

    return {
      success: true,
      isIncluded,
      error: null
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to get fee allocation';
    console.error('Error getting fee allocation:', error);
    return {
      success: false,
      isIncluded: null,
      error: errorMessage
    };
  }
};

/**
 * Update fee allocation settings for trip-level fees
 * @param {number} tripId - The trip ID
 * @param {FeeAllocation} allocations - Object with fee type allocations
 * @returns {Promise<{success: boolean, data: Array<Fee>|null, error: string|null}>} API response
 */
export const updateTripFeeAllocations = async (tripId, allocations) => {
  try {
    // First, get the current trip fees to get their IDs
    const { data: tripFees, error: fetchError } = await getTripFees(tripId);
    if (fetchError) {
      throw new Error(fetchError || 'Failed to fetch trip fees');
    }

    // Create array of fee updates
    /** @type {Array<{feeid: number, isincluded: boolean}>} */
    const feesToUpdate = [];

    // If trip has no fees, fall back to grouping global fees so we can save
    let sourceFees = Array.isArray(tripFees) ? tripFees : [];
    if (sourceFees.length === 0) {
      const { data: globalData, error: globalErr } = await getGlobalFees();
      if (globalErr || !globalData) {
        throw new Error(globalErr || 'Failed to fetch global fees');
      }
      sourceFees = globalData.allFees || [];
    }

    // Group fees by paymentref, normalizing null/empty to 'platform'
    const byRef = sourceFees.reduce((acc, fee) => {
      const raw = fee?.paymentref;
      const key =
        raw === null || raw === undefined || raw === '' ? 'platform' : raw;
      acc[key] = acc[key] || [];
      acc[key].push(fee);
      return acc;
    }, {});

    const applyGroup = (groupKey, allocationValue) => {
      if (!allocationValue) return; // skip if not provided
      const group = byRef[groupKey];
      if (!Array.isArray(group) || group.length === 0) return;
      const include = allocationValue === 'organizer';
      group.forEach((fee) => {
        if (fee?.feeid) {
          feesToUpdate.push({ feeid: fee.feeid, isincluded: include });
        }
      });
    };

    applyGroup('card', allocations.creditCardFeeAllocation);
    applyGroup('us_bank_account', allocations.achFeeAllocation);
    applyGroup('platform', allocations.platformFeeAllocation);

    // Update the fees
    const { data: updatedFees, error: updateError } = await updateTripFees(
      tripId,
      feesToUpdate
    );

    if (updateError) {
      throw new Error(updateError);
    }

    return {
      success: true,
      data: updatedFees || [],
      error: null
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to update trip fee allocations';
    console.error('Error updating trip fee allocations:', error);
    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};
